// src/components/Dogs/VaccinationManager.jsx
import { useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { updateDog } from '../../redux/slices/Dogs/dogsSlice';

const EMPTY_FORM = { vaccineName: '', dateGiven: '', nextDueDate: '', notes: '' };

const VaccinationManager = ({ dog, onUpdate }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const vaccinations = dog.vaccinations || [];

  // Calculate days since a given date
  const daysSince = (dateString) => {
    const given = new Date(dateString);
    const today = new Date();
    const diffTime = today - given;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays : 0;
  };

  // Get status based on nextDueDate
  const getVaccineStatus = (nextDueDate) => {
    if (!nextDueDate) return 'unknown';
    const today = new Date();
    const due = new Date(nextDueDate);
    const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 30) return 'dueSoon';
    return 'upToDate';
  };

  const statusColors = {
    overdue: 'text-[#9A0002] bg-[#9A0002]/10',
    dueSoon: 'text-[#8A6A3A] bg-[#B08D57]/15',
    upToDate: 'text-[#3A6B4C] bg-[#3A6B4C]/10',
    unknown: 'text-[#3A2A20]/50 bg-[#3A2A20]/5',
  };

  // Guarda el array completo de vacunas en el backend (create o edit).
  // El array COMPLETO se manda siempre porque el endpoint reemplaza el
  // campo "vaccinations" tal cual - no hace merge por índice.
  const persistVaccinations = async (newVaccinations) => {
    setSubmitting(true);
    try {
      const updatedDog = await dispatch(
        updateDog({ id: dog._id, dogData: { vaccinations: newVaccinations } })
      ).unwrap();
      onUpdate(updatedDog);
      toast.success(t('vaccinationManager.saved'));
      return true;
    } catch (error) {
      toast.error(error || t('vaccinationManager.saveError'));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddOrUpdate = async () => {
    if (!formData.vaccineName || !formData.dateGiven || !formData.nextDueDate) {
      toast.error(t('vaccinationManager.requiredFields'));
      return;
    }

    let newVaccinations;
    if (editingIndex !== null) {
      newVaccinations = [...vaccinations];
      newVaccinations[editingIndex] = { ...vaccinations[editingIndex], ...formData };
    } else {
      newVaccinations = [...vaccinations, { ...formData }];
    }

    const success = await persistVaccinations(newVaccinations);
    if (success) resetForm();
  };

  const handleDelete = async (index) => {
    const newVaccinations = vaccinations.filter((_, i) => i !== index);
    await persistVaccinations(newVaccinations);
  };

  const handleEdit = (index) => {
    const vac = vaccinations[index];
    setFormData({
      vaccineName: vac.vaccineName,
      dateGiven: vac.dateGiven?.split('T')[0] || '',
      nextDueDate: vac.nextDueDate?.split('T')[0] || '',
      notes: vac.notes || '',
    });
    setEditingIndex(index);
    setIsAdding(true);
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setFormData(EMPTY_FORM);
  };

  const inputClass =
    'w-full px-3 py-2 border border-[#3A2A20]/15 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition';

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-['Fraunces'] text-base font-semibold text-[#3A2A20]">
          {t('vaccinationManager.title')}
        </h4>
        <button
          onClick={() => setIsAdding(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#9A0002] text-[#EFE6DD] rounded-lg hover:bg-[#7A0001] transition"
        >
          <PlusIcon className="h-3.5 w-3.5" /> {t('vaccinationManager.add')}
        </button>
      </div>

      {/* Vaccination Table */}
      {vaccinations.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-[#3A2A20]/10">
          <table className="min-w-full divide-y divide-[#3A2A20]/10 text-sm">
            <thead className="bg-[#EFE6DD]/60">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                  {t('dogDetailsModal.vaccine')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                  {t('vaccinationManager.dateGiven')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                  {t('dogDetailsModal.daysSince')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                  {t('vaccinationManager.nextDue')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                  {t('dogDetailsModal.status')}
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                  {t('vaccinationManager.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3A2A20]/10 bg-white">
              {vaccinations.map((vac, idx) => {
                const status = getVaccineStatus(vac.nextDueDate);
                return (
                  <tr key={vac._id || idx}>
                    <td className="px-3 py-2.5 font-medium text-[#3A2A20]">{vac.vaccineName}</td>
                    <td className="px-3 py-2.5 text-[#3A2A20]/70">
                      {new Date(vac.dateGiven).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2.5 text-[#3A2A20]/70">
                      {daysSince(vac.dateGiven)} {t('dogDetailsModal.days')}
                    </td>
                    <td className="px-3 py-2.5 text-[#3A2A20]/70">
                      {vac.nextDueDate ? new Date(vac.nextDueDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[status]}`}>
                        {t(`dogDetailsModal.${status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => handleEdit(idx)}
                          disabled={submitting}
                          className="text-[#3A5A7A] hover:text-[#2A4560] disabled:opacity-40 transition"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(idx)}
                          disabled={submitting}
                          className="text-[#9A0002] hover:text-[#7A0001] disabled:opacity-40 transition"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[#3A2A20]/50 text-sm py-4 text-center bg-[#EFE6DD]/30 rounded-xl">
          {t('vaccinationManager.noRecords')}
        </p>
      )}

      {/* Add/Edit Form Modal */}
      {isAdding && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20]">
                {editingIndex !== null
                  ? t('vaccinationManager.editTitle')
                  : t('vaccinationManager.addTitle')}
              </h3>
              <button onClick={resetForm} className="text-[#3A2A20]/40 hover:text-[#3A2A20] transition">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="text"
                placeholder={t('vaccinationManager.vaccineNamePlaceholder')}
                value={formData.vaccineName}
                onChange={(e) => setFormData({ ...formData, vaccineName: e.target.value })}
                className={inputClass}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50 mb-1">
                    {t('vaccinationManager.dateGiven')}
                  </label>
                  <input
                    type="date"
                    value={formData.dateGiven}
                    onChange={(e) => setFormData({ ...formData, dateGiven: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50 mb-1">
                    {t('vaccinationManager.nextDue')}
                  </label>
                  <input
                    type="date"
                    value={formData.nextDueDate}
                    onChange={(e) => setFormData({ ...formData, nextDueDate: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <textarea
                placeholder={t('vaccinationManager.notesPlaceholder')}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className={inputClass}
                rows="2"
              />
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={resetForm}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-[#3A2A20] border border-[#3A2A20]/20 rounded-lg hover:bg-[#3A2A20]/5 transition disabled:opacity-50"
              >
                {t('vaccinationManager.cancel')}
              </button>
              <button
                onClick={handleAddOrUpdate}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-[#9A0002] text-[#EFE6DD] rounded-lg hover:bg-[#7A0001] transition disabled:opacity-50"
              >
                {submitting
                  ? t('vaccinationManager.saving')
                  : editingIndex !== null
                  ? t('vaccinationManager.update')
                  : t('vaccinationManager.addAction')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaccinationManager;